import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Megaphone, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import StatusBadge from '@/components/platform/StatusBadge'
import {
  useCampaigns,
  useCampaign,
  useCreateCampaign,
  useUpdateCampaign,
  useDeleteCampaign,
} from '@/hooks/data/useCampaigns'
import type { Campaign, CampaignInput } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'

const toDateInput = (iso: string) => iso.slice(0, 10)
const toIsoDate = (value: string) => new Date(`${value}T00:00:00.000Z`).toISOString()

function CampaignsPage() {
  const { data: campaigns = [], isLoading, isError, error, isFetching, refetch } = useCampaigns()
  const create = useCreateCampaign()
  const update = useUpdateCampaign()
  const del = useDeleteCampaign()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<Campaign | null>(null)

  // The list response omits `description` (only present on GET /campaigns/{id} per the
  // shared-core type). Fetch the full record on edit and hold the modal closed until it
  // arrives, rather than prefilling blank and racing an admin's in-progress typing.
  const editingDetail = useCampaign(editing?.id)
  const editModalReady = !!editing && editingDetail.isSuccess

  const rows = campaigns.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()))

  const fields = [
    { key: 'code', label: 'Code', required: true, placeholder: 'SUMMER25' },
    { key: 'name', label: 'Name', required: true },
    { key: 'description', label: 'Description', type: 'textarea' as const, required: true },
    { key: 'discountPercent', label: 'Discount %', type: 'number' as const, required: true, placeholder: '1–50' },
    { key: 'startsAt', label: 'Starts', placeholder: 'YYYY-MM-DD', required: true },
    { key: 'endsAt', label: 'Ends', placeholder: 'YYYY-MM-DD', required: true },
    {
      key: 'active',
      label: 'Status',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ]

  const toCampaignInput = (values: Record<string, any>, maxRedemptions?: number | null): CampaignInput => ({
    code: values.code,
    name: values.name,
    description: values.description,
    discountPercent: Number(values.discountPercent),
    startsAt: toIsoDate(values.startsAt),
    endsAt: toIsoDate(values.endsAt),
    active: values.active === 'true',
    maxRedemptions: maxRedemptions ?? undefined,
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6" /> Campaigns
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage promo-code campaigns (CampaignsController).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Campaign
          </button>
        </div>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search campaigns…"
        className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading campaigns…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load campaigns: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No campaigns found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Campaign</th>
                <th className="px-4 py-2">Discount</th>
                <th className="px-4 py-2">Starts</th>
                <th className="px-4 py-2">Ends</th>
                <th className="px-4 py-2">Redemptions</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const isLoadingDetail = editing?.id === c.id && !editingDetail.isSuccess
                return (
                  <tr key={c.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800 dark:text-gray-100">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.code}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{c.discountPercent}%</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(c.startsAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(c.endsAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.redemptions}
                      {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.active ? 'Active' : 'Inactive'} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing(c)}
                          disabled={isLoadingDetail}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-40"
                          title="Edit campaign"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            confirmDelete(c.name, async () => {
                              await del.mutateAsync(c.id)
                            })
                          }
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                          title="Delete campaign"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Campaign"
        fields={fields}
        initialValues={{ active: 'true' }}
        submitLabel="Create"
        onSubmit={async (values) => {
          await create.mutateAsync(toCampaignInput(values))
        }}
      />

      <EditModal
        isOpen={editModalReady}
        onClose={() => setEditing(null)}
        title="Edit Campaign"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing && editingDetail.data
            ? {
                code: editing.code,
                name: editing.name,
                description: editingDetail.data.description ?? '',
                discountPercent: editing.discountPercent,
                startsAt: toDateInput(editing.startsAt),
                endsAt: toDateInput(editing.endsAt),
                active: editing.active ? 'true' : 'false',
              }
            : {}
        }
        onSubmit={async (values) => {
          const id = editing?.id
          if (!id) return
          await update.mutateAsync({ id, body: toCampaignInput(values, editing?.maxRedemptions) })
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/campaigns/')({
  component: CampaignsPage,
})
